/*
 * <scroll-carousel> — reusable arrow navigation for a scroll-snap carousel.
 * Native horizontal scroll + scroll-snap own the movement; this module only:
 *   - wires the prev/next arrows to move exactly one card at a time;
 *   - disables an arrow at each end (no infinite loop);
 *   - hides the arrows entirely when the cards already fit;
 *   - adds mouse drag via the shared assets/carousel-drag.js (touch already
 *     swipes natively).
 * Shared by the Collection carousel and the Content carousel.
 * Guarded so repeated sections only define the element once.
 */
// Bare name, resolved by the import map in layout/theme.liquid to the
// versioned asset URL (a relative './' path would bypass cache-busting).
import { enableDrag, snapPositions } from 'carousel-drag';

if (!customElements.get('scroll-carousel')) {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  class ScrollCarousel extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('[data-cc-track]');
      if (!this.track) return;
      this.prevBtn = this.querySelector('[data-cc-prev]');
      this.nextBtn = this.querySelector('[data-cc-next]');

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.track.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onResize);

      // Once per element: the theme editor moves nodes, which runs this again
      // on the same buttons, and two listeners would move two cards a press.
      if (!this.wired) {
        this.wired = true;
        if (this.prevBtn) this.prevBtn.addEventListener('click', () => this.step(-1));
        if (this.nextBtn) this.nextBtn.addEventListener('click', () => this.step(1));
      }

      this.drag = enableDrag(this.track);

      this.update();
    }

    disconnectedCallback() {
      if (!this.track) return;
      this.track.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      cancelAnimationFrame(this.raf);
      if (this.drag) this.drag.destroy();
    }

    /* Move exactly one card: to the first snap stop beyond the current
       position in the chosen direction. Going by stops rather than by a
       computed distance means a press from an off-snap position (after a
       drag, say) lands cleanly on the next card instead of drifting, and the
       clamped end of the track counts as one final stop. The small tolerance
       keeps sub-pixel rounding from re-selecting the stop we're already on. */
    step(direction) {
      const stops = snapPositions(this.track);
      const x = this.track.scrollLeft;
      const tolerance = 2;
      const target =
        direction > 0
          ? stops.find((stop) => stop > x + tolerance)
          : [...stops].reverse().find((stop) => stop < x - tolerance);
      if (target === undefined) return; // already at that end
      this.track.scrollTo({
        left: target,
        behavior: reduce.matches ? 'auto' : 'smooth',
      });
    }

    onScroll() {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.update());
    }

    onResize() {
      cancelAnimationFrame(this.raf);
      this.raf = requestAnimationFrame(() => this.update());
    }

    update() {
      const maxScroll = this.track.scrollWidth - this.track.clientWidth;
      const x = this.track.scrollLeft;
      const noOverflow = maxScroll <= 1;
      this.classList.toggle('carousel--no-overflow', noOverflow);
      // aria-disabled, not the disabled property: a disabled button can't hold
      // focus, so the arrow a keyboard user has just pressed to reach the last
      // card would drop their focus to the top of the page. An arrow at its end
      // looks the same and says "dimmed"; pressing it does nothing (step()
      // finds no further stop).
      this.setOff(this.prevBtn, noOverflow || x <= 1);
      this.setOff(this.nextBtn, noOverflow || x >= maxScroll - 1);
    }

    setOff(button, off) {
      if (!button) return;
      if (off) button.setAttribute('aria-disabled', 'true');
      else button.removeAttribute('aria-disabled');
    }
  }

  customElements.define('scroll-carousel', ScrollCarousel);
}
